/**
 * Layer 1: Gateway Edge Engine (GEE) — POLISHED
 * Armadillo Adamantium Security Framework
 * 
 * Responsibility: Normalize incoming requests, extract security signals,
 * build SecurityContext for downstream security layers.
 * 
 * Polish additions:
 * - HTTP/2 and HTTP/3 fingerprinting
 * - Pseudo-header order analysis
 * - Header canonicalization scoring
 * - Request-shape scoring
 */

import { sha256 } from 'js-sha256';
import { SecurityContext, RequestShape } from './types';

/**
 * Normalize an HTTP request into a SecurityContext
 * Extracts IP, user agent, fingerprints, and other signals
 */
export async function normalizeRequest(req: Request): Promise<SecurityContext> {
  // Extract IP address
  const ip = req.headers.get('x-forwarded-for')?.split(',')[0]?.trim() ?? 
             req.headers.get('x-real-ip') ?? 
             'unknown';
  
  // Extract user agent
  const ua = req.headers.get('user-agent') || '';
  
  // Parse URL
  const url = new URL(req.url);
  
  // Build fingerprint from browser signals
  const fingerprintRaw = `${ua}|${req.headers.get('accept-language')}|${req.headers.get('sec-ch-ua')}|${req.headers.get('sec-ch-ua-platform')}`;
  const fingerprintHash = await sha256(fingerprintRaw + ((typeof process !== 'undefined' ? (process as any).env?.FP_SALT : null) || 'armadillo-fp-salt'));
  
  // Hash IP address for privacy-preserving lookups
  const ipHash = await sha256(ip + ((typeof process !== 'undefined' ? (process as any).env?.IP_SALT : null) || 'armadillo-ip-salt'));
  
  // Generate session ID from fingerprint + IP
  const sessionId = await sha256(fingerprintHash + ip);
  
  // Extract capability token from headers or cookies
  const authHeader = req.headers.get('authorization') || '';
  const capabilityToken = authHeader.startsWith('Bearer ') 
    ? authHeader.substring(7) 
    : null;
  
  // Extract entropy nonce (client-side timing jitter)
  const entropyNonce = req.headers.get('x-entropy-nonce') || '';
  
  // Polish: Extract protocol-level intelligence
  const protocolVersion = detectProtocolVersion(req);
  const pseudoHeaderOrder = extractPseudoHeaderOrder(req);
  const http2Fingerprint = extractHTTP2Fingerprint(req);
  const http3Fingerprint = extractHTTP3Fingerprint(req);
  const headerCanonicalizationScore = computeHeaderCanonicalizationScore(req);
  const requestShapeScore = computeRequestShapeScore(req);

  // Build security context
  const ctx: SecurityContext = {
    requestId: crypto.randomUUID(),
    timestamp: Date.now(),
    ip,
    ipHash,
    fingerprintHash,
    userAgent: ua,
    path: url.pathname,
    method: req.method,
    riskScore: 0,              // Will be computed by Adaptive Risk Engine
    trustTier: 'untrusted',    // Default until token verification
    entropyNonce,
    sessionId,
    capabilityToken,
    headers: Object.fromEntries(req.headers.entries()),
    protocolVersion,
    pseudoHeaderOrder,
    requestShapeScore,
    headerCanonicalizationScore,
    http2Fingerprint,
    http3Fingerprint,
  };
  
  return ctx;
}

/**
 * Extract additional client signals from request
 * Used for enhanced fingerprinting
 */
export function extractClientSignals(req: Request): Record<string, string> {
  return {
    acceptLanguage: req.headers.get('accept-language') || '',
    secChUa: req.headers.get('sec-ch-ua') || '',
    secChUaPlatform: req.headers.get('sec-ch-ua-platform') || '',
    secChUaMobile: req.headers.get('sec-ch-ua-mobile') || '',
    secFetchDest: req.headers.get('sec-fetch-dest') || '',
    secFetchMode: req.headers.get('sec-fetch-mode') || '',
    secFetchSite: req.headers.get('sec-fetch-site') || '',
    dnt: req.headers.get('dnt') || '',
    connection: req.headers.get('connection') || '',
    cacheControl: req.headers.get('cache-control') || ''
  };
}

/**
 * Check if request is from a known bot/crawler
 */
export function isBot(userAgent: string): boolean {
  const botPatterns = [
    /googlebot/i,
    /bingbot/i,
    /slurp/i,
    /duckduckbot/i,
    /baiduspider/i,
    /yandexbot/i,
    /facebookexternalhit/i,
    /twitterbot/i,
    /rogerbot/i,
    /linkedinbot/i,
    /embedly/i,
    /showyoubot/i,
    /outbrain/i,
    /pinterest/i,
    /slackbot/i,
    /discordbot/i,
    /whatsapp/i
  ];
  
  return botPatterns.some(pattern => pattern.test(userAgent));
}

/**
 * Calculate request entropy (timing jitter)
 * Higher entropy = more likely human, lower = more likely bot
 */
export function calculateEntropy(entropyNonce: string, timestamp: number): number {
  if (!entropyNonce) return 0.5; // Default uncertainty
  
  try {
    // Parse nonce (format: "timestamp:jitter:counter")
    const parts = entropyNonce.split(':');
    if (parts.length !== 3) return 0.5;
    
    const clientTimestamp = parseInt(parts[0]);
    const jitter = parseInt(parts[1]);
    const counter = parseInt(parts[2]);
    
    // Calculate timing variance
    const serverTimestamp = timestamp;
    const latency = Math.abs(serverTimestamp - clientTimestamp);
    
    // Human timing has higher variance (50-500ms)
    // Bot timing is very consistent (<10ms or >1000ms)
    const humanRange = latency >= 50 && latency <= 500;
    const jitterQuality = jitter > 0 && jitter < 100;
    
    if (humanRange && jitterQuality) {
      return 0.9; // High entropy = likely human
    } else if (latency < 10 || latency > 1000) {
      return 0.2; // Low entropy = likely bot
    }
    
    return 0.5; // Uncertain
  } catch {
    return 0.5;
  }
}

/**
 * Validate request structure
 * Returns true if request is well-formed
 */
export function validateRequest(req: Request): boolean {
  // Must have user agent
  if (!req.headers.get('user-agent')) {
    return false;
  }
  
  // Must have valid method
  const validMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS', 'HEAD'];
  if (!validMethods.includes(req.method.toUpperCase())) {
    return false;
  }
  
  // Must have valid URL
  try {
    new URL(req.url);
  } catch {
    return false;
  }
  
  return true;
}

// ============================================
// POLISH: PROTOCOL-LEVEL INTELLIGENCE
// ============================================

/**
 * Detect HTTP protocol version from request headers
 * Vercel Edge provides hints via :protocol pseudo-header or x-forwarded-proto
 */
export function detectProtocolVersion(req: Request): string {
  // Check for HTTP/2 indicators
  if (req.headers.has(':authority') || req.headers.has('x-http2-stream-id')) {
    return 'h2';
  }
  // Check for HTTP/3 (QUIC) indicators
  if (req.headers.has('alt-svc') && req.headers.get('alt-svc')?.includes('h3')) {
    return 'h3';
  }
  // Vercel Edge typically normalizes to HTTP/1.1 representation
  return req.headers.get('x-forwarded-proto') === 'https' ? 'h2' : 'http/1.1';
}

/**
 * Extract pseudo-header order from request
 * HTTP/2 uses :method, :path, :scheme, :authority in specific order
 * Browsers and tools send them in different orders
 */
export function extractPseudoHeaderOrder(req: Request): string {
  // In edge runtime, we can observe header ordering from the raw header list
  const headerNames: string[] = [];
  req.headers.forEach((_v, k) => headerNames.push(k.toLowerCase()));
  
  // Hash the first 8 header names as order fingerprint
  const orderSig = headerNames.slice(0, 8).join(',');
  // Simple hash for fast comparison
  let hash = 0;
  for (let i = 0; i < orderSig.length; i++) {
    hash = ((hash << 5) - hash + orderSig.charCodeAt(i)) | 0;
  }
  return hash.toString(16);
}

/**
 * Extract HTTP/2 SETTINGS frame fingerprint
 * Different clients send different SETTINGS parameters
 */
export function extractHTTP2Fingerprint(req: Request): string | undefined {
  // In edge runtime, we can't directly read SETTINGS frames
  // But we can infer from header patterns that correlate with H2 client implementations
  const h2Signals: string[] = [];
  
  // Chrome sends these in specific order
  if (req.headers.has('sec-ch-ua')) h2Signals.push('chromium');
  if (req.headers.has('sec-fetch-dest')) h2Signals.push('fetch-api');
  if (req.headers.has('priority')) h2Signals.push('priority-hints');
  
  // Firefox patterns
  if (req.headers.get('user-agent')?.includes('Firefox')) h2Signals.push('firefox');
  
  if (h2Signals.length === 0) return undefined;
  return h2Signals.join(':');
}

/**
 * Extract HTTP/3 (QUIC) transport parameter fingerprint
 */
export function extractHTTP3Fingerprint(req: Request): string | undefined {
  // QUIC-specific headers
  if (req.headers.has('x-quic-stream-id') || req.headers.has('alt-used')) {
    const signals: string[] = [];
    if (req.headers.has('alt-used')) signals.push('alt-used');
    if (req.headers.has('x-quic-stream-id')) signals.push('quic-stream');
    return signals.join(':');
  }
  return undefined;
}

/**
 * Compute header canonicalization score
 * Checks if headers follow normal browser ordering conventions
 * Anomalous ordering suggests non-browser clients
 */
export function computeHeaderCanonicalizationScore(req: Request): number {
  const headerNames: string[] = [];
  req.headers.forEach((_v, k) => headerNames.push(k.toLowerCase()));
  
  // Normal browser header order (approximate):
  // host, connection, accept, user-agent, accept-language, accept-encoding, ...
  const expectedFirst = ['host', 'connection', 'accept', 'user-agent'];
  const expectedEarly = ['accept-language', 'accept-encoding', 'sec-ch-ua', 'sec-fetch-dest'];
  
  let score = 1.0;
  
  // Check if expected headers appear early
  for (const expected of expectedFirst) {
    const idx = headerNames.indexOf(expected);
    if (idx >= 0 && idx > 3) score -= 0.15;
  }
  
  // Check if expected-early headers appear in first 8 positions
  for (const expected of expectedEarly) {
    const idx = headerNames.indexOf(expected);
    if (idx >= 0 && idx > 8) score -= 0.1;
  }
  
  // Penalize very few headers (scanner/bot behavior)
  if (headerNames.length < 5) score -= 0.3;
  
  return Math.max(0, Math.min(1, score));
}

/**
 * Compute request shape score
 * Analyzes the overall "shape" of the request for anomalies
 */
export function computeRequestShapeScore(req: Request): number {
  const url = new URL(req.url);
  let score = 1.0;
  
  // Query string complexity (too many params = suspicious)
  const paramCount = url.searchParams ? Array.from(url.searchParams.keys()).length : 0;
  if (paramCount > 20) score -= 0.3;
  else if (paramCount > 10) score -= 0.1;
  
  // Path depth (very deep paths are unusual)
  const pathDepth = url.pathname.split('/').filter(Boolean).length;
  if (pathDepth > 8) score -= 0.2;
  
  // Content-Type mismatch
  const method = req.method.toUpperCase();
  const hasBody = ['POST', 'PUT', 'PATCH'].includes(method);
  const contentType = req.headers.get('content-type');
  if (hasBody && !contentType) score -= 0.15;
  if (!hasBody && contentType) score -= 0.1;
  
  // Accept header missing (all browsers send Accept)
  if (!req.headers.get('accept')) score -= 0.2;
  
  // Accept-Language missing (browsers always send this)
  if (!req.headers.get('accept-language')) score -= 0.1;
  
  // Sec-Fetch-* headers missing (modern browsers always send these)
  if (!req.headers.get('sec-fetch-mode')) score -= 0.15;
  if (!req.headers.get('sec-fetch-site')) score -= 0.1;
  
  return Math.max(0, Math.min(1, score));
}

/**
 * Compute comprehensive request shape analysis
 */
export function computeFullRequestShape(req: Request): RequestShape {
  const url = new URL(req.url);
  const headerNames: string[] = [];
  req.headers.forEach((_v, k) => headerNames.push(k.toLowerCase()));
  
  const headerOrderScore = computeHeaderCanonicalizationScore(req);
  
  // Method/path consistency
  const method = req.method.toUpperCase();
  const hasBody = ['POST', 'PUT', 'PATCH'].includes(method);
  const contentType = req.headers.get('content-type');
  const methodPathConsistency = (hasBody && contentType) || (!hasBody && !contentType) ? 1.0 : 0.5;
  
  // Content length variance
  const contentLength = parseInt(req.headers.get('content-length') || '0');
  const contentLengthVariance = contentLength > 1048576 ? 0.3 : contentLength > 102400 ? 0.6 : 1.0;
  
  // Query complexity
  const paramCount = url.searchParams ? Array.from(url.searchParams.keys()).length : 0;
  const queryComplexityScore = paramCount > 20 ? 0.3 : paramCount > 10 ? 0.6 : 1.0;
  
  const overallScore = (headerOrderScore + methodPathConsistency + contentLengthVariance + queryComplexityScore) / 4;
  
  return { headerOrderScore, methodPathConsistency, contentLengthVariance, queryComplexityScore, overallScore };
}
